import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $findMatchingParent, mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { Dispatch, useCallback, useEffect, useState, FC } from 'react'
import * as React from 'react'
import { Box, Button, Popper, TextField, Link } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import { getSelectedNode } from '../../utils/getSelectedNode'
import { sanitizeUrl } from '../../utils/url'
import { EMPTY_LINK } from './const'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(1),
    background: theme.shape.markdownEditor.background,
    boxShadow: theme.shape.markdownEditor.shadowPopover,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    padding: theme.spacing(1),
    minWidth: 0,
    minHeight: 0,
    marginRight: theme.spacing(1),
    svg: {
      height: 'auto',
    },
  },
  buttonIcon: {
    width: 16,
  },
  buttonsContainer: {
    marginLeft: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
  },
}))

interface FloatingLinkEditorPluginProps {
  isLinkEditMode: boolean
  setIsLinkEditMode: Dispatch<boolean>
}

/**
 * Popover for adding/updating external link for selected text
 */
export const FloatingLinkEditorPlugin: FC<FloatingLinkEditorPluginProps> = ({ isLinkEditMode, setIsLinkEditMode }) => {
  const [editor] = useLexicalComposerContext()
  const [linkUrl, setLinkUrl] = useState('')
  const [editedLinkUrl, setEditedLinkUrl] = useState(EMPTY_LINK)
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { classes } = useStyles()

  useEffect(() => {
    const updateToolbar = (): void => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection)
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode)
        const focusAutoLinkNode = $findMatchingParent(focusNode, $isAutoLinkNode)
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setAnchor(null)
          return
        }
        const badNode = selection.getNodes().find(node => {
          const linkNode = $findMatchingParent(node, $isLinkNode)
          const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode)
          return !linkNode?.is(focusLinkNode) && !autoLinkNode?.is(focusAutoLinkNode) && !linkNode && !autoLinkNode && !$isLineBreakNode(node)
        })
        if (!badNode) {
          const selectedAnchorElement = editor.getElementByKey(selection.anchor.key)

          setAnchor(selectedAnchorElement)
        } else {
          setAnchor(null)
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        _payload => {
          updateToolbar()
          return false
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        payload => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection)
            const linkNode = $findMatchingParent(node, $isLinkNode)
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank')
              return true
            }
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor])

  const handleLinkSubmission = (): void => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl))
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent()
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              })
              parent.replace(linkNode, true)
            }
          }
        })
      }
      setEditedLinkUrl(EMPTY_LINK)
      setIsLinkEditMode(false)
    }
  }

  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleLinkSubmission()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setIsLinkEditMode(false)
    }
  }

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const linkParent = $findMatchingParent(node, $isLinkNode)

      if (linkParent) {
        setLinkUrl(linkParent.getURL())
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL())
      } else {
        setLinkUrl('')
      }
      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl)
      }
    }
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    const rootElement = editor.getRootElement()

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      setLastSelection(selection)
    } else if (!activeElement) {
      setAnchor(null)
      setLastSelection(null)
      setIsLinkEditMode(false)
      setLinkUrl('')
    }

    return true
  }, [editor, setIsLinkEditMode, isLinkEditMode, linkUrl])

  useEffect(() => {
    const scrollerElem = anchor?.parentElement

    const update = (): void => {
      editor.getEditorState().read(() => {
        updateLinkEditor()
      })
    }

    window.addEventListener('resize', update)

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update)
      }
    }
  }, [anchor?.parentElement, editor, updateLinkEditor])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor()
          return true
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (anchor) {
            setAnchor(null)
            return true
          }
          return false
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [editor, updateLinkEditor, setAnchor, anchor])

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor()
    })
  }, [editor, updateLinkEditor])

  return (
    <Popper
      open={Boolean(anchor)}
      anchorEl={anchor}
      popperOptions={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 15],
            },
          },
        ],
      }}
      placement="bottom-start"
      sx={{
        zIndex: 9999,
      }}
      /** TODO add missing values */
      // placeholder={undefined}
      // onPointerEnterCapture={undefined}
      // onPointerLeaveCapture={undefined}
    >
      <Box className={classes.container}>
        {isLinkEditMode ? (
          <>
            <TextField
              value={editedLinkUrl}
              onChange={event => {
                setEditedLinkUrl(event.target.value)
              }}
              onKeyDown={event => {
                monitorInputInteraction(event)
              }}
              size="small"
            />
            <Box className={classes.buttonsContainer}>
              <Button
                onMouseDown={event => event.preventDefault()}
                className={classes.button}
                onClick={() => {
                  setIsLinkEditMode(false)
                  setAnchor(null)
                }}
              >
                <CloseIcon className={classes.buttonIcon} />
              </Button>
              <Button onMouseDown={event => event.preventDefault()} onClick={handleLinkSubmission} className={classes.button}>
                <CheckIcon className={classes.buttonIcon} />
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Link href={sanitizeUrl(linkUrl)} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </Link>
            <Box className={classes.buttonsContainer}>
              <Button
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  setEditedLinkUrl(linkUrl)
                  setIsLinkEditMode(true)
                }}
                className={classes.button}
              >
                <EditIcon className={classes.buttonIcon} />
              </Button>
              <Button
                onMouseDown={event => event.preventDefault()}
                onClick={() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
                }}
                className={classes.button}
              >
                <DeleteIcon className={classes.buttonIcon} />
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Popper>
  )
}
